import React, { useCallback, useMemo, useId } from 'react';
import { TextField } from '@mui/material';
import FieldLabel from './FieldLabel';
import { ILabel } from '../constants/interfaces';
import { fieldTypes } from '../constants/app';
import {
  getBaseTextFieldStyles,
  getAccessibilityProps,
  getFormHelperTextSlotProps,
  getFieldName,
  getComputedFieldValues,
  inputLimits,
} from '../styles/fieldStyles';

/**
 * Props for the NumberField component
 */
export interface INumberFieldProps {
  /** Current number value as string */
  value: string;
  /** Callback fired when the number value changes */
  onChange: (value: string) => void;
  /** Field configuration containing label text, validation rules, etc. */
  field: ILabel;
  /** Error message to display */
  errorText?: string;
  /** Whether to hide the label */
  isLabelHidden?: boolean;
  /** Whether the field is read-only/display only */
  displayOnly?: boolean;
  /** Width of the number input */
  width?: string;
  /** Whether to allow decimal values */
  allowDecimal?: boolean;
  /** Number of decimal places allowed */
  decimalPlaces?: number;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Step value for increment/decrement */
  step?: number;
  /** Maximum character length */
  maxLength?: number;
  /** Callback fired when input gains focus */
  onFocus?: () => void;
  /** Callback fired when input loses focus */
  onBlur?: () => void;
}

/**
 * Utility functions for number input handling
 */
const numberUtils = {
  /**
   * Formats number for display
   */
  formatNumber: (value: string, allowDecimal: boolean): string => {
    if (!value) return '';

    const num = Number(value);
    if (isNaN(num)) return value;

    if (allowDecimal) {
      return num.toLocaleString('en-US', {
        maximumFractionDigits: 10,
        useGrouping: false,
      });
    }

    return Math.floor(num).toString();
  },

  /**
   * Gets the appropriate input mode for mobile keyboards
   */
  getInputMode: (allowDecimal: boolean): string => {
    return allowDecimal ? 'decimal' : 'numeric';
  },

  /**
   * Checks if value is within bounds
   */
  isWithinBounds: (value: string, min?: number, max?: number): boolean => {
    if (!value) return true;

    const num = Number(value);
    if (isNaN(num)) return false;

    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;

    return true;
  },

  /**
   * Sanitizes numeric input based on decimal settings
   */
  sanitizeNumericInput: (
    value: string,
    allowDecimal: boolean,
    decimalPlaces: number
  ): string => {
    // Remove all non-numeric characters except decimal point if allowed
    let sanitized = value.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');

    if (allowDecimal) {
      // Only allow one decimal point
      const firstDot = sanitized.indexOf('.');
      if (firstDot !== -1) {
        sanitized =
          sanitized.substring(0, firstDot + 1) +
          sanitized.substring(firstDot + 1).replace(/\./g, '');
      }

      // Limit decimal places
      if (sanitized.includes('.')) {
        const [intPart, decPart] = sanitized.split('.');
        sanitized =
          intPart + '.' + (decPart ? decPart.slice(0, decimalPlaces) : '');
      }
    }

    // Prevent leading zeros unless immediately followed by a dot
    if (/^0[0-9]+/.test(sanitized)) {
      sanitized = sanitized.replace(/^0+/, '0');
    }

    return sanitized;
  },

  /**
   * Validates number value based on field requirements
   */
  validateNumber: (
    value: string,
    field: ILabel,
    min?: number,
    max?: number
  ): string | null => {
    if (field.isMandatory && !value.trim()) {
      return `${field.text} is required`;
    }

    if (value && isNaN(Number(value))) {
      return `${field.text} must be a valid number`;
    }

    const numValue = Number(value);
    if (min !== undefined && numValue < min) {
      return `${field.text} must be at least ${min}`;
    }

    if (max !== undefined && numValue > max) {
      return `${field.text} must be at most ${max}`;
    }

    return null;
  },
};

/**
 * NumberField Component
 *
 * A reusable numeric input component that integrates with form validation
 * and provides a consistent interface for number input across the application.
 *
 * Features:
 * - Integer and decimal number support
 * - Configurable decimal places
 * - Min/max value validation
 * - Automatic input sanitization
 * - Accessibility support with labels and ARIA attributes
 * - Consistent styling with the design system
 * - Mobile-optimized numeric keyboards
 * - Memoized performance optimizations
 *
 * @example
 * ```tsx
 * <NumberField
 *   value={price}
 *   onChange={handlePriceChange}
 *   field={priceField}
 *   errorText={validationError}
 *   allowDecimal={true}
 *   decimalPlaces={2}
 *   min={0}
 *   max={99999}
 * />
 * ```
 */
function NumberField(props: INumberFieldProps) {
  const {
    value,
    onChange,
    field,
    errorText,
    isLabelHidden = false,
    displayOnly = false,
    width,
    allowDecimal = field.type === fieldTypes.decimal ||
      field.type === fieldTypes.float,
    decimalPlaces = 2,
    disabled = false,
    readOnly = false,
    placeholder,
    min,
    max,
    step = allowDecimal ? 0.01 : 1,
    maxLength = inputLimits.maxLength.number,
    onFocus,
    onBlur,
  } = props;

  // Generate unique ID for accessibility
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  // Memoize computed values using centralized utility plus specific values
  const computedValues = useMemo(
    () => ({
      ...getComputedFieldValues(
        errorText,
        disabled,
        displayOnly,
        width,
        maxLength
      ),
      inputMode: numberUtils.getInputMode(allowDecimal),
      isWithinBounds: numberUtils.isWithinBounds(value, min, max),
    }),
    [
      errorText,
      disabled,
      displayOnly,
      width,
      maxLength,
      value,
      min,
      max,
      allowDecimal,
    ]
  );

  // Optimized change handler with useCallback
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const sanitizedValue = numberUtils.sanitizeNumericInput(
        newValue,
        allowDecimal,
        decimalPlaces
      );
      onChange(sanitizedValue);
    },
    [onChange, allowDecimal, decimalPlaces]
  );

  // Optimized focus handler
  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  // Optimized blur handler
  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  return (
    <>
      {/* Label with proper accessibility attributes */}
      <FieldLabel field={field} htmlFor={fieldId} isHidden={isLabelHidden} />

      {/* Number input with enhanced props */}
      <TextField
        id={fieldId}
        fullWidth={String(computedValues.fieldWidth) === '100%'}
        name={getFieldName(field)}
        error={computedValues.hasError}
        size="small"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={computedValues.isDisabled}
        helperText={computedValues.helperText}
        type="text"
        placeholder={placeholder}
        sx={getBaseTextFieldStyles(computedValues.fieldWidth)}
        inputProps={{
          inputMode: computedValues.inputMode,
          max: max,
          maxLength: computedValues.effectiveMaxLength,
          min: min,
          pattern: allowDecimal ? '[0-9]*\\.?[0-9]*' : '[0-9]*',
          step: step,
          ...getAccessibilityProps(
            fieldId,
            labelId,
            errorId,
            field,
            isLabelHidden,
            computedValues.hasError
          ),
          readOnly: readOnly,
        }}
        slotProps={getFormHelperTextSlotProps(computedValues.hasError, errorId)}
      />
    </>
  );
}

export default NumberField;

// Export utilities for testing and external use
export { numberUtils };
